import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { resolveImageUrl } from '../../lib/utils';

const EASE = [0.22, 1, 0.36, 1];

/** A mill logo, or a clean wordmark when none has been uploaded yet. */
export const MillMark = ({ mill, className = 'max-h-12 md:max-h-14' }) =>
  mill.logo ? (
    <img
      src={resolveImageUrl(mill.logo)}
      alt={mill.name}
      loading="lazy"
      className={`${className} w-auto object-contain`}
    />
  ) : (
    <span className="px-display text-lg md:text-xl font-bold uppercase tracking-[0.1em] text-[var(--px-ink)] leading-tight">
      {mill.name}
    </span>
  );

/**
 * The mills we work with most closely. Shown as its own tier so a principal mill never
 * reads like an occasional source. Order and membership are managed in admin.
 */
export const PrincipalMills = ({ mills, heading }) => {
  const { t } = useLanguage();
  if (!mills.length) return null;

  return (
    <section className="bg-[var(--px-paper)] border-b px-hair py-20 md:py-28" data-testid="principal-mills">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 lg:gap-24 items-end mb-12">
          <p className="px-data text-[11px] tracking-[0.25em] text-[var(--px-ink-soft)]">
            [ 01 ] {heading || t('mills.kicker')}
          </p>
          <div className="max-w-3xl">
            <h2 className="px-display text-2xl md:text-4xl leading-tight font-semibold text-[var(--px-ink)]">
              {t('mills.homeHeading')}
            </h2>
            <p className="text-sm text-[var(--px-ink-soft)] mt-4 leading-relaxed max-w-xl">
              {t('mills.homeText')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 border-t border-s px-hair">
          {mills.map((m, i) => (
            <motion.div
              key={m.id || m.name}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-5%' }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.07 }}
              data-testid={`principal-mill-${i}`}
              className="group relative border-b border-e px-hair bg-[var(--px-surface)] p-6 md:p-7 flex flex-col hover:bg-[var(--px-paper)] transition-colors duration-500"
            >
              <div className="h-14 flex items-center">
                <MillMark mill={m} />
              </div>
              <p className="px-data text-[9px] tracking-[0.2em] uppercase text-[var(--px-ink-soft)] mt-6">
                {m.country}
              </p>
              <p className="text-sm text-[var(--px-ink)] mt-2 leading-relaxed flex-1">
                {m.supplies || m.note}
              </p>
              {m.relationship && m.relationship !== 'stock' && (
                <p className="px-data text-[9px] tracking-[0.16em] uppercase text-[var(--px-accent)] mt-5 pt-4 border-t px-hair">
                  {t(`mills.rel.${m.relationship}`)}
                </p>
              )}
            </motion.div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-8">
          <Link
            to="/mills"
            data-testid="principal-mills-all"
            className="px-data inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[var(--px-ink)] hover:text-[var(--px-accent)] transition-colors"
          >
            {t('mills.seeAll')} <ArrowRight className="w-3.5 h-3.5 rtl-flip" />
          </Link>
          <p className="text-[11px] text-[var(--px-ink-soft)]">{t('mills.homeNote')}</p>
        </div>
      </div>
    </section>
  );
};
